import React, { Suspense, useEffect, useState } from "react";
import { usePatron } from "../../hooks/usePatron";
import { componentMap, componentNames } from "../../config/componentMap";
import { typeMap } from "../../config/typeMap";
import { PatronDataTypesRecord, PropField } from "../../types/global";
import { getQueryString } from "../../helpers/getQueryString";
import ErrorHandler from "../ErrorHandler/ErrorHandler";
import { WidgetProps } from "./WidgetTypes";

const Widget: React.FC<WidgetProps> = ({
  widgetName,
  patronId,
  customProps
}) => {
  const extractedPatronId = patronId || getQueryString(window.location.href).id;
  const { patronData, loading } = usePatron(extractedPatronId);

  if (loading) {
    return;
  }

  const fields = patronData && !patronData.error ? patronData.fields : [];

  const [error, setError] = useState<string>("");
  const [props, setProps] = useState<PatronDataTypesRecord>({});
  const [DynamicComponent, setDynamicComponent] =
    useState<React.ComponentType<PatronDataTypesRecord> | null>(null);

  const importDynamicComponent = (widget: string) => {
    return componentMap[widget];
  };

  const validateProps = async (
    widgetProps: PatronDataTypesRecord,
    widget: string
  ) => {
    if (!typeMap[widget]) {
      throw new Error(`No type definition for widget "${widget}".`);
    }

    const { default: dynamicType } = await typeMap[widget]();
    dynamicType.parse(widgetProps);
  };

  const hasProps = Object.keys(props).length > 0;

  useEffect(() => {
    const loadAndValidateProps = async (widgetProps: PatronDataTypesRecord) => {
      try {
        await validateProps(widgetProps, widgetName);
        setProps(widgetProps);
      } catch (validationError) {
        setError(`Validation error: ${validationError}`);
      }
    };

    const loadComponent = async () => {
      if (patronData?.error) {
        setError(patronData.error);
        return;
      }

      if (!componentNames.includes(widgetName)) {
        setError(`Widget "${widgetName}" does not exist.`);
        return;
      }

      const Component = importDynamicComponent(widgetName);

      if (!Component) {
        setError(`Failed to load component "${widgetName}".`);
        return;
      }

      setDynamicComponent(() => Component);

      if (extractedPatronId && fields) {
        const mappedFields = (fields as PropField[]).reduce((acc, field) => {
          acc[field.key] = field.value;
          return acc;
        }, {} as PatronDataTypesRecord);

        await loadAndValidateProps(mappedFields);
      } else if (customProps) {
        await loadAndValidateProps(customProps);
      }
    };

    loadComponent();
  }, [widgetName, extractedPatronId, fields, customProps]);

  if (error) {
    return <ErrorHandler error={error} />;
  }

  if (!DynamicComponent) {
    return <div>Laddar...</div>;
  }

  if (extractedPatronId && !hasProps) {
    return;
  }

  return (
    <Suspense fallback={<div>Laddar...</div>}>
      <DynamicComponent {...props} />
    </Suspense>
  );
};

export default Widget;
